import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { CalendarDays, List, Plus, Truck } from 'lucide-react'

import { AdasCalendar } from '@components/wrapper-components'
import { SchedularCalendarEvent } from '@components/scheduler/schedule-calendar-event'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { getEvents, getScheduleAvailability } from 'api/api'
import { DATE_FORMATS } from 'constants'
import { WeekdaysEnum, weekdaysEnumToFCIndex } from 'enums/enums'
import { Logger } from 'logger'
import { daysOfWeekMap, getTimeZoneByCode, mapEventsToCalendarFormat } from 'utils/utils'
import { schedularStatusStyles, statusOptions } from '../schedule-calendar/helper'
import './MobileScheduler.css'

dayjs.extend(utc)
dayjs.extend(timezone)

const STATUS_LABEL = Object.fromEntries(statusOptions.map((s) => [s.id, s.name]))

const getInitials = (name) => {
  if (!name) return '?'
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return parts[0][0]?.toUpperCase() || '?'
}

/** Monday-anchored start of the week containing `date`. */
const startOfWeekMonday = (date) => {
  const d = dayjs(date)
  const dow = d.day() // 0=Sun … 6=Sat
  const daysToMonday = (dow === 0 ? -6 : 1) - dow
  return d.add(daysToMonday, 'day').startOf('day')
}

const StatusBadge = ({ type }) => {
  const styles = schedularStatusStyles[type] || schedularStatusStyles.SCHEDULED
  return (
    <span
      className='inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[12px] font-bold'
      style={{ backgroundColor: styles.backgroundColor, color: styles.color }}
    >
      <span className='inline-block h-1.5 w-1.5 rounded-full' style={{ backgroundColor: styles.borderColor }} />
      {STATUS_LABEL[type] || 'Scheduled'}
    </span>
  )
}
StatusBadge.propTypes = { type: PropTypes.string }

const AgendaCard = ({ event, offset, onOpen }) => {
  const ext = event.extendedProps || {}
  const start = dayjs(ext.start).tz(offset)
  const end = dayjs(ext.end).tz(offset)
  const vehicle = ext.vehicle || {}
  const vehicleLine = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
  const ro = vehicle.repair_order_num ? `RO #${vehicle.repair_order_num}` : ''
  const assigneeName = ext.assignee?.full_name || ''
  const styles = schedularStatusStyles[ext.type] || schedularStatusStyles.SCHEDULED

  return (
    <button
      type='button'
      onClick={() => onOpen?.(event.id)}
      className='flex w-full items-stretch gap-3 rounded-xl border border-[#EBEDF0] bg-white p-3 text-left shadow-sm transition-colors hover:bg-[#F8FAFC]'
    >
      <div className='flex w-[64px] flex-none flex-col pt-0.5'>
        <span className='text-[15px] font-bold leading-tight text-[#0A1628]'>{start.format('h:mm A')}</span>
        <span className='mt-0.5 text-[13px] leading-tight text-[#6B7280]'>{end.format('h:mm A')}</span>
      </div>
      <span className='w-[3px] flex-none self-stretch rounded-full' style={{ backgroundColor: styles.borderColor }} />
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-2'>
          <StatusBadge type={ext.type} />
          {ext.deliveryPickup && (
            <span className='inline-flex flex-none items-center gap-1 text-[12px] font-semibold text-[#6B7280]'>
              <Truck className='h-3.5 w-3.5' />
              P&amp;D
            </span>
          )}
        </div>
        <div className='mt-1 truncate text-[16px] font-bold leading-tight text-[#0A1628]'>
          {ext.client?.business_name || ext.client?.full_name || 'Appointment'}
        </div>
        {(vehicleLine || ro) && (
          <div className='mt-0.5 truncate text-[13px] text-[#6B7280]'>{[vehicleLine, ro].filter(Boolean).join(' · ')}</div>
        )}
        {assigneeName && (
          <div className='mt-2 flex items-center gap-1.5'>
            <span className='inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#DBEAFE] text-[10px] font-bold text-[#1F2D3D]'>
              {getInitials(assigneeName)}
            </span>
            <span className='truncate text-[13px] text-[#0A1628]'>{assigneeName}</span>
          </div>
        )}
      </div>
    </button>
  )
}
AgendaCard.propTypes = {
  event: PropTypes.object.isRequired,
  offset: PropTypes.string,
  onOpen: PropTypes.func
}

export const MobileScheduler = ({ refresh, openNewEventSlider, onEventOpen, onCountChange }) => {
  const ccId = useCenterStore((store) => store.ccId)
  const organisationId = useOrganisationStore((store) => store.organisationId)
  const organisationDetails = useOrganisationStore((state) => state.organisationDetail)
  const timeZone = getTimeZoneByCode(organisationDetails?.timezone_at)

  const calendarRef = useRef(null)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [mobileView, setMobileView] = useState('agenda') // 'agenda' | 'day'
  const [weekEvents, setWeekEvents] = useState([])
  const [businessHours, setBusinessHours] = useState()
  const [hiddenDays, setHiddenDays] = useState([])

  const weekStart = useMemo(() => startOfWeekMonday(selectedDate), [selectedDate])
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')), [weekStart])

  const fetchWeek = useCallback(async () => {
    if (!ccId || !organisationId) return
    const startDate = weekStart.format(DATE_FORMATS.ISO_DATE)
    const endDate = weekStart.add(6, 'day').format(DATE_FORMATS.ISO_DATE)
    try {
      const response = await getEvents({ ccId, organisationId, startDate, endDate })
      if (response?.status === 200) setWeekEvents(mapEventsToCalendarFormat(response.data))
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'MobileScheduler', method: 'fetchWeek' } })
    }
  }, [ccId, organisationId, weekStart])

  useEffect(() => {
    fetchWeek()
  }, [fetchWeek, refresh])

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!ccId || !organisationId) return
      try {
        const response = await getScheduleAvailability({ ccId, organisation_id: organisationId })
        const availability = response?.data
        if (Array.isArray(availability) && availability.length) {
          setHiddenDays(
            availability
              .filter(
                (d) =>
                  d.start_time[0] === 0 &&
                  d.start_time[1] === 0 &&
                  d.end_time[0] === 0 &&
                  d.end_time[1] === 0 &&
                  (d.day === 'SATURDAY' || d.day === 'SUNDAY')
              )
              .map((d) => weekdaysEnumToFCIndex[WeekdaysEnum[d.day]])
          )
          setBusinessHours(
            availability
              .filter((d) => !(d.start_time[0] === 0 && d.end_time[0] === 0))
              .map((d) => ({
                daysOfWeek: [daysOfWeekMap[d.day]],
                startTime: `${String(d.start_time[0]).padStart(2, '0')}:${String(d.start_time[1]).padStart(2, '0')}`,
                endTime: `${String(d.end_time[0]).padStart(2, '0')}:${String(d.end_time[1]).padStart(2, '0')}`
              }))
          )
        }
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'MobileScheduler', method: 'fetchAvailability' } })
      }
    }
    fetchAvailability()
  }, [ccId, organisationId])

  // Events that start on the selected day, sorted chronologically.
  const dayEvents = useMemo(() => {
    const key = dayjs(selectedDate).format('YYYY-MM-DD')
    return weekEvents
      .filter((e) => dayjs(e.extendedProps?.start).tz(timeZone).format('YYYY-MM-DD') === key)
      .sort((a, b) => dayjs(a.extendedProps?.start).valueOf() - dayjs(b.extendedProps?.start).valueOf())
  }, [weekEvents, selectedDate, timeZone])

  // Set of YYYY-MM-DD that have at least one appointment (drives the week-strip dots).
  const daysWithEvents = useMemo(() => {
    const set = new Set()
    weekEvents.forEach((e) => set.add(dayjs(e.extendedProps?.start).tz(timeZone).format('YYYY-MM-DD')))
    return set
  }, [weekEvents, timeZone])

  useEffect(() => {
    if (onCountChange) onCountChange(weekEvents.length, dayEvents.length)
  }, [weekEvents.length, dayEvents.length, onCountChange])

  // Keep the FullCalendar day view anchored to the selected date.
  useEffect(() => {
    if (mobileView !== 'day' || !calendarRef.current) return
    calendarRef.current.getApi().gotoDate(dayjs(selectedDate).format(DATE_FORMATS.ISO_DATE))
  }, [selectedDate, mobileView])

  const bookedHours = useMemo(() => {
    const mins = dayEvents.reduce((sum, e) => sum + (e.extendedProps?.duration || 0), 0)
    if (!mins) return ''
    const h = mins / 60
    return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`
  }, [dayEvents])

  const renderEventContent = (eventInfo) => {
    const { title, extendedProps } = eventInfo.event
    const { id, type, assignee, start, client, offset, vehicle, duration } = extendedProps
    const timeText = dayjs(start).tz(offset).format(DATE_FORMATS.ISO_TIME_STRING)
    const repair_order_num = duration >= 90 ? vehicle?.repair_order_num : ''
    return (
      <SchedularCalendarEvent
        timeText={timeText}
        type={type}
        title={title}
        id={id}
        assingeeName={assignee?.full_name || ''}
        fullName={client?.business_name || client?.full_name || ''}
        vehicleYear={vehicle?.year || ''}
        vehicleMake={vehicle?.make || ''}
        repair_order_num={repair_order_num}
        duration={duration}
      />
    )
  }

  const handleEventClick = (info) => {
    if (info?.event?.id && onEventOpen) onEventOpen(info.event.id)
  }

  const appointmentLabel = `${dayEvents.length} ${dayEvents.length === 1 ? 'appointment' : 'appointments'}`

  return (
    <div className='mobile-scheduler relative'>
      {/* Week strip */}
      <div className='grid grid-cols-7 border-b border-[#EBEDF0] pb-3'>
        {weekDays.map((d) => {
          const key = d.format('YYYY-MM-DD')
          const isSelected = d.isSame(dayjs(selectedDate), 'day')
          const hasEvents = daysWithEvents.has(key)
          return (
            <button key={key} type='button' onClick={() => setSelectedDate(d.toDate())} className='flex flex-col items-center gap-1 py-1'>
              <span className={`text-[11px] font-bold uppercase tracking-wide ${isSelected ? 'text-[#0A1628]' : 'text-[#6B7280]'}`}>
                {d.format('ddd')}
              </span>
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-lg text-[16px] font-bold ${
                  isSelected ? 'bg-[#1F2D3D] text-white' : 'text-[#0A1628]'
                }`}
              >
                {d.format('D')}
              </span>
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  isSelected ? (hasEvents ? 'bg-white' : 'bg-transparent') : hasEvents ? 'bg-[#1F6FEB]' : 'bg-transparent'
                }`}
              />
            </button>
          )
        })}
      </div>

      {/* Day header + Day/Agenda toggle */}
      <div className='flex items-start justify-between gap-3 py-3'>
        <div className='min-w-0'>
          <div className='truncate text-[18px] font-bold leading-tight text-[#0A1628]'>{dayjs(selectedDate).format('dddd, MMMM D')}</div>
          <div className='mt-0.5 text-[13px] text-[#6B7280]'>
            {appointmentLabel}
            {bookedHours && ` · ${bookedHours} booked`}
          </div>
        </div>
        <div className='inline-flex flex-none items-center rounded-lg bg-[#EBEDF0] p-0.5'>
          {[
            { key: 'day', label: 'Day', Icon: CalendarDays },
            { key: 'agenda', label: 'Agenda', Icon: List }
          ].map(({ key, label, Icon }) => {
            const active = mobileView === key
            return (
              <button
                key={key}
                type='button'
                onClick={() => setMobileView(key)}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-bold transition-colors ${
                  active ? 'bg-white text-[#0A1628] shadow-sm' : 'text-[#6B7280]'
                }`}
              >
                <Icon className='h-4 w-4' />
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Body */}
      {mobileView === 'agenda' ? (
        <div className='space-y-3 pb-24'>
          {dayEvents.length === 0 ? (
            <div className='rounded-xl border border-dashed border-[#D1D5D8] bg-[#F8FAFC] py-12 text-center text-[14px] text-[#6B7280]'>
              No appointments on this day.
            </div>
          ) : (
            dayEvents.map((e) => <AgendaCard key={e.id} event={e} offset={timeZone} onOpen={onEventOpen} />)
          )}
        </div>
      ) : (
        <div className='pb-24'>
          <AdasCalendar
            view='timeGridDay'
            SchedularCalendarEvent={SchedularCalendarEvent}
            calendarRef={calendarRef}
            updateTitle={() => {}}
            setIsSliderOpen={() => {}}
            setSelectedEvent={() => {}}
            initialDate={dayjs(selectedDate).format(DATE_FORMATS.ISO_DATE)}
            events={dayEvents}
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            nowIndicator={true}
            slotHeight='48px'
            slotMinTime='00:00:00'
            slotMaxTime='24:00:00'
            allDaySlot={false}
            scrollTime='07:00'
            height={720}
            timeZone={timeZone}
            hiddenDays={hiddenDays}
            businessHours={businessHours}
            firstDay={1}
            eventMaxStack={5}
            dayHeaders={false}
          />
        </div>
      )}

      {/* Floating New FAB */}
      <button
        type='button'
        onClick={openNewEventSlider}
        className='fixed bottom-6 right-5 z-30 inline-flex items-center gap-2 rounded-full bg-[#1F2D3D] px-6 py-3.5 text-[15px] font-bold text-white shadow-lg transition-opacity hover:opacity-90'
      >
        <Plus className='h-5 w-5' />
        New
      </button>
    </div>
  )
}

MobileScheduler.propTypes = {
  refresh: PropTypes.bool,
  openNewEventSlider: PropTypes.func,
  onEventOpen: PropTypes.func,
  onCountChange: PropTypes.func
}
